<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  metrics: { type: Object, default: null },
  analysis: { type: Object, default: null },
  analysisLoading: { type: Boolean, default: false },
  analysisDisabled: { type: Boolean, default: false },
});
const emit = defineEmits(['analyze']);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        Reação Pública (Comentários)
      </h3>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-sparkles"
        :is-loading="analysisLoading"
        :disabled="analysisDisabled"
        @click="emit('analyze')"
      >
        Analisar
      </Button>
    </div>

    <div v-if="metrics" class="grid grid-cols-3 gap-2 mb-3">
      <div class="rounded-lg bg-emerald-50 p-2 text-center">
        <p class="text-[10px] uppercase text-emerald-700">Positivos</p>
        <p class="text-lg font-bold text-emerald-800">
          {{ metrics.sentiment.positive }}
        </p>
      </div>
      <div class="rounded-lg bg-rose-50 p-2 text-center">
        <p class="text-[10px] uppercase text-rose-700">Negativos</p>
        <p class="text-lg font-bold text-rose-800">
          {{ metrics.sentiment.negative }}
        </p>
      </div>
      <div class="rounded-lg bg-n-slate-3 p-2 text-center">
        <p class="text-[10px] uppercase text-n-slate-11">Neutros</p>
        <p class="text-lg font-bold text-n-slate-12">
          {{ metrics.sentiment.neutral }}
        </p>
      </div>
    </div>

    <div
      v-if="analysisLoading && !analysis"
      class="flex items-center justify-center py-4"
    >
      <Spinner />
    </div>
    <div v-else-if="analysis" class="space-y-3">
      <div>
        <h4 class="mb-1 text-xs font-semibold text-n-slate-12">
          Percepção Pública
        </h4>
        <p class="text-xs text-n-slate-11">{{ analysis.publicPerception }}</p>
      </div>

      <div v-if="analysis.mainDemands && analysis.mainDemands.length">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-12">
          Principais Demandas
        </h4>
        <ul class="space-y-0.5 text-xs text-n-slate-11">
          <li v-for="(d, i) in analysis.mainDemands" :key="`d-${i}`">
            • {{ d }}
          </li>
        </ul>
      </div>

      <div v-if="analysis.emotionalTone && analysis.emotionalTone.length">
        <h4 class="mb-1 text-xs font-semibold text-n-slate-12">
          Tom Emocional
        </h4>
        <div class="space-y-1">
          <div
            v-for="(t, i) in analysis.emotionalTone"
            :key="`t-${i}`"
            class="flex items-center gap-2 text-xs"
          >
            <span class="w-24 text-n-slate-11">{{ t.tone }}</span>
            <div class="flex-1 overflow-hidden rounded-full bg-n-slate-3">
              <div
                class="h-1.5 rounded-full bg-blue-500"
                :style="{ width: `${t.percentage}%` }"
              />
            </div>
            <span class="w-10 text-right text-n-slate-11"
              >{{ t.percentage }}%</span
            >
          </div>
        </div>
      </div>

      <div v-if="analysis.supportBase" class="grid grid-cols-2 gap-2">
        <div class="rounded-lg border border-emerald-200 p-2">
          <h4
            class="mb-0.5 text-[10px] font-semibold uppercase text-emerald-700"
          >
            Apoiadores
          </h4>
          <p class="text-xs text-n-slate-11">{{ analysis.supportBase }}</p>
        </div>
        <div class="rounded-lg border border-rose-200 p-2">
          <h4 class="mb-0.5 text-[10px] font-semibold uppercase text-rose-700">
            Críticos
          </h4>
          <p class="text-xs text-n-slate-11">{{ analysis.criticBase }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
